import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Subject } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { environment } from '@qwyk/portals/environment';

interface QueuedRequest<T> {
    url: string;
    resolve: (value: T) => void;
    reject: (error: unknown) => void;
}

@Injectable({ providedIn: 'root' })
export class LazyLoadQueueService {
    private readonly _queue$ = new Subject<QueuedRequest<unknown>>();
    private readonly _cache = new Map<string, unknown>();
    private readonly _pendingRequests = new Map<string, Promise<unknown>>();

    constructor(private readonly _http: HttpClient) {
        this._queue$
            .pipe(
                concatMap((request) => this._processRequest(request))
            )
            .subscribe();
    }

    load<T>(rawUrl: string): Promise<T> {
        const url = LazyLoadQueueService._toLocalDevUrl(rawUrl);

        // If we have cached data, return it immediately
        if (this._cache.has(url)) {
            return Promise.resolve(this._cache.get(url) as T);
        }

        // If there's already a pending request for this URL, return that promise
        if (this._pendingRequests.has(url)) {
            return this._pendingRequests.get(url) as Promise<T>;
        }

        // Create new request
        const promise = new Promise<T>((resolve, reject) => {
            this._queue$.next({
                url,
                resolve: resolve as (value: unknown) => void,
                reject,
            });
        });

        this._pendingRequests.set(url, promise);
        return promise;
    }

    /**
     * The backend returns lazy-load URLs as absolute (`https://api.<env>.qwykportals.com/...`),
     * which is correct for a deployed frontend on the same origin family. Local
     * dev instead runs the Angular dev-server behind a proxy that forwards
     * *relative* `/api/...` requests to the remote backend — an absolute URL
     * bypasses that proxy and calls the remote host directly from
     * `*.localhost`, which then 401s (the bearer-token interceptor's
     * `allowedDomains` only matches `localhost`, so it won't attach auth to a
     * request that already targets a different host).
     *
     * The `-proxy` environments (`beta-proxy`/`next-proxy`/`prod-proxy`, used
     * by `serve:portals-client:*`) are the only ones with `backendServer: ''`
     * — every real deployment sets a real absolute `backendServer`. That flag
     * is therefore a reliable "running locally" signal: strip the URL's
     * origin so it becomes relative and rides the same proxy as every other
     * request. Real deployments never hit this branch.
     */
    private static _toLocalDevUrl(url: string): string {
        if (environment.backendServer !== '' || !/^https?:\/\//i.test(url)) {
            return url;
        }
        try {
            const parsed = new URL(url);
            return parsed.pathname + parsed.search;
        } catch {
            return url;
        }
    }

    private _processRequest<T>(request: QueuedRequest<T>): Promise<void> {
        return this._http
            .get<T>(request.url)
            .toPromise()
            .then((response) => {
                this._cache.set(request.url, response);
                this._pendingRequests.delete(request.url);
                request.resolve(response);
            })
            .catch((err) => {
                this._pendingRequests.delete(request.url);
                request.reject(err);
            });
    }
}
